/**
 * BulkDeleteConfirmationDialog
 *
 * Confirmation dialog for bulk delete operations. Shows a scrollable list
 * of item names, handles partial failures, and reports results.
 */

import { useEffect, useRef } from 'react';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './BulkDeleteConfirmationDialog.css';

export interface BulkDeleteConfirmationDialogProps {
  open: boolean;
  itemNames: string[];
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
  result?: { succeeded: string[]; failed: Array<{ id: string; error: string }> } | null;
  onDone?: () => void;
}

export function BulkDeleteConfirmationDialog({
  open,
  itemNames,
  onConfirm,
  onCancel,
  isDeleting = false,
  result,
  onDone,
}: BulkDeleteConfirmationDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
  }, [open]);

  const handleDialogClose = (): void => {
    if (!isDeleting) {
      if (result) {
        onDone?.();
      } else {
        onCancel();
      }
    }
  };

  const showResult = result !== null && result !== undefined;

  return (
    <DialogElement
      ref={dialogRef}
      onForge-dialog-close={handleDialogClose}
      className="bulk-delete-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="bulk-delete-title"
      aria-describedby="bulk-delete-desc"
    >
      <div className="dialog-content">
        {!showResult ? (
          <>
            <div className="dialog-header">
              <forge-icon name="warning" className="dialog-icon" aria-hidden="true"></forge-icon>
              <h2 id="bulk-delete-title" className="forge-typography--headline5">
                Delete {itemNames.length} {itemNames.length === 1 ? 'item' : 'items'}?
              </h2>
            </div>

            <div id="bulk-delete-desc" className="dialog-body">
              <p className="forge-typography--body1">
                The following items will be soft deleted (marked as Retired):
              </p>
              <ul className="bulk-delete-item-list">
                {itemNames.map((name, i) => (
                  <li key={i} className="forge-typography--body2">
                    {name}
                  </li>
                ))}
              </ul>
              <div className="dialog-warning">
                <forge-icon name="error" aria-hidden="true"></forge-icon>
                <p className="forge-typography--body2">This action cannot be undone.</p>
              </div>
            </div>

            <div className="dialog-actions">
              <ForgeButton variant="text" onClick={onCancel} disabled={isDeleting} type="button">
                Cancel
              </ForgeButton>
              <ForgeButton
                variant="raised"
                className="bulk-delete-dialog__confirm"
                onClick={onConfirm}
                disabled={isDeleting}
                type="button"
              >
                {isDeleting ? 'Deleting...' : `Delete ${itemNames.length} items`}
              </ForgeButton>
            </div>
          </>
        ) : (
          <>
            <div className="dialog-header">
              <forge-icon
                name={result.failed.length === 0 ? 'check_circle' : 'warning'}
                className={`dialog-icon ${result.failed.length === 0 ? 'dialog-icon--success' : ''}`}
                aria-hidden="true"
              ></forge-icon>
              <h2 id="bulk-delete-title" className="forge-typography--headline5">
                Deletion complete
              </h2>
            </div>

            <div id="bulk-delete-desc" className="dialog-body">
              <p className="forge-typography--body1">
                <strong>{result.succeeded.length}</strong> deleted successfully.
              </p>
              {result.failed.length > 0 && (
                <div className="bulk-delete-result-failures">
                  <p className="forge-typography--body1">
                    <strong>{result.failed.length}</strong> failed:
                  </p>
                  <ul className="bulk-delete-error-list">
                    {result.failed.map(({ id, error }) => (
                      <li key={id} className="forge-typography--body2">
                        <span className="bulk-delete-error-id">{id}</span>: {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="dialog-actions">
              <ForgeButton variant="raised" onClick={onDone} type="button">
                Done
              </ForgeButton>
            </div>
          </>
        )}
      </div>
    </DialogElement>
  );
}
